import React, { useState } from 'react';
import { apiPostFile } from './../../services/api';
import { Box, Typography, Button, Paper, CircularProgress } from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';

const CookieUploader = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [statusMessage, setStatusMessage] = useState("");
    const [isUploading, setIsUploading] = useState(false);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setStatusMessage(`File selected: ${file.name}`);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setStatusMessage('Please select a file first.');
            return;
        }
        
        // ✅ START: GET USER ID FROM LOCAL STORAGE
        let userId = null;
        try {
            const userString = localStorage.getItem('user');
            if (!userString) {
                setStatusMessage('❌ Error: Could not find user information. Please log in again.');
                return;
            }
            const user = JSON.parse(userString);
            userId = user?.id; // Or user?._id, depending on your user object structure

            if (!userId) {
                setStatusMessage('❌ Error: User ID is missing. Please log in again.');
                return;
            }
        } catch (error) {
            setStatusMessage('❌ Error: Could not read user data.');
            return;
        }
        // ✅ END: GET USER ID

        const formData = new FormData();
        formData.append('cookiesFile', selectedFile);
        // ✅ APPEND THE USER ID TO THE FORM DATA
        formData.append('userId', userId);
        
        setIsUploading(true);
        setStatusMessage('Uploading...');

        try {
            const result = await apiPostFile('/admin/upload-cookies', formData);

            if (result && result.success) {
                setStatusMessage('✅ Success! Cookies have been renewed.');
            } else {
                setStatusMessage(`❌ Error: ${result.error || 'Upload failed.'}`);
            }
        } catch (error) {
            setStatusMessage(`❌ Network Error: ${error.message}`);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Paper 
                elevation={0} 
                variant="outlined"
                sx={{ 
                    p: 4, 
                    maxWidth: '700px', 
                    margin: '40px auto',
                    borderRadius: 3,
                    textAlign: 'center'
                }}
            >
                <Typography variant="h4" sx={{ fontWeight: 600, color: "#4896a1", mb: 1 }}>
                    Renew Authentication Cookies
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    Upload the `cookies.json` file you generated to re-authenticate your account with the application.
                </Typography>

                <Paper 
                    elevation={0}
                    sx={{
                        p: 4,
                        border: '2px dashed #e0e0e0',
                        borderRadius: 2,
                        bgcolor: '#f9f9f9',
                        mb: 3
                    }}
                >
                    <CloudUploadIcon sx={{ fontSize: 50, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.primary" gutterBottom>
                        {selectedFile ? selectedFile.name : "Click button to select file"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Please make sure the file is named 'cookies.json'
                    </Typography>
                    <Button
                        component="label"
                        variant="outlined"
                        sx={{ mt: 2, color: "#4D99A3", borderColor: "#4D99A3" }}
                    >
                        Browse File
                        <input
                            type="file"
                            accept=".json"
                            hidden
                            onChange={handleFileChange}
                        />
                    </Button>
                </Paper>

                <Button
                    onClick={handleUpload}
                    variant="contained"
                    disabled={!selectedFile || isUploading}
                    sx={{ 
                        backgroundColor: "#4D99A3", 
                        color: "white",
                        padding: '10px 30px',
                        fontSize: '1rem'
                    }}
                >
                    {isUploading ? <CircularProgress size={24} color="inherit" /> : 'Upload and Renew'}
                </Button>

                {statusMessage && (
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            mt: 3, 
                            fontWeight: 'bold',
                            color: statusMessage.includes('Error') ? 'error.main' : 'text.primary'
                        }}
                    >
                        {statusMessage}
                    </Typography>
                )}
            </Paper>
        </Box>
    );
};

export default CookieUploader;